/**
 * Compliance Reviewer modal.
 *
 * Usage in Template Editor:
 *   <Button onClick={() => setOpen(true)} leftIcon={<span>🛡️</span>}>Compliance check (AI)</Button>
 *   <ComplianceReviewerModal isOpen={open} onClose={() => setOpen(false)}
 *      defaults={{ template_text: formData.smsBody, channel: "sms", category: "MARKETING" }} />
 *
 * Local-only.
 */
import React, { useState } from "react";
import {
  Box,
  Text,
  Heading,
  HStack,
  VStack,
  Code,
  Badge,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Input,
  Grid,
  GridItem
} from "@chakra-ui/react";
import { FiShield } from "react-icons/fi";
import AIAgentModal from "./AIAgentModal";
import { labelForCategory } from "./templateCategory";

const CHANNELS = ["sms", "whatsapp", "rcs", "email", "push", "voice", "inapp"];
// Keep the empty option for the "no category selected" case (the
// agent then applies channel-only rules). FEAT-0011 adds INFORMATIONAL
// so all four Template Types from the Create Template page are
// representable in the compliance modal.
const CATEGORIES = ["", "AUTHENTICATION", "UTILITY", "MARKETING", "INFORMATIONAL"];

const VERDICT_INFO = {
  compliant:    { color: "green",   label: "Compliant — safe to send" },
  warnings:     { color: "yellow",  label: "Compliant with warnings" },
  needs_review: { color: "blue",    label: "Needs human review" },
  blocked:      { color: "red",     label: "Blocked — do not send" }
};

export default function ComplianceReviewerModal({ isOpen, onClose, defaults = {} }) {
  const [text,     setText]     = useState(defaults.template_text || "");
  const [channel,  setChannel]  = useState(defaults.channel || "sms");
  const [category, setCategory] = useState(defaults.category || "");
  const [templateId, setTemplateId] = useState(defaults.template_id || "");

  const formContent = (
    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Channel</FormLabel>
        <Select size="sm" value={channel} onChange={(e) => setChannel(e.target.value)}>
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Template Category</FormLabel>
        <Select size="sm" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c ? labelForCategory(c) : "— (channel-only rules)"}</option>)}
        </Select>
      </FormControl>
      <GridItem colSpan={2}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Template ID (from MCP) — optional</FormLabel>
          <Input size="sm" value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                 placeholder="leave blank to use raw text below" />
        </FormControl>
      </GridItem>
      <GridItem colSpan={2}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Template body</FormLabel>
          <Textarea size="sm" rows={4} fontFamily="mono" fontSize="13px"
                    value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Hi {{1}}, your code is {{2}}. Reply STOP to unsubscribe." />
        </FormControl>
      </GridItem>
    </Grid>
  );

  return (
    <AIAgentModal
      isOpen={isOpen}
      onClose={onClose}
      agentId="compliance-reviewer"
      title="Compliance Review (AI)"
      description="Safe Message Filter blocklist + WhatsApp HSM rules + generic channel rules + LLM nuance."
      icon={<Box w={9} h={9} borderRadius="lg" bg="orange.50" display="flex" alignItems="center" justifyContent="center"><FiShield color="#F59E0B" size={18} /></Box>}
      autoRun={false}
      ctaLabel="Run compliance check"
      formContent={formContent}
      fields={{
        template_id: templateId || undefined,
        template_text: text || undefined,
        channel,
        category: category || undefined
      }}
      renderResult={(r) => <Result result={r} />}
    />
  );
}

function Result({ result }) {
  const info = VERDICT_INFO[result.verdict] || { color: "gray", label: result.verdict };
  return (
    <VStack align="stretch" spacing={3}>
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
        <HStack justify="space-between" mb={2}>
          <Heading size="sm">Verdict</Heading>
          <Badge colorScheme={info.color} fontSize="sm" px={3} py={1} borderRadius="full">{info.label}</Badge>
        </HStack>
        <HStack fontSize="xs" color="gray.600" spacing={4}>
          <Text>errors: <strong>{result.errors}</strong></Text>
          <Text>warnings: <strong>{result.warnings}</strong></Text>
          <Text>channel: <strong>{result.channel}</strong></Text>
          {result.template_category && <Text>category: <strong>{result.template_category}</strong></Text>}
          <Text>safe-msg:&nbsp;
            {result.safe_message_filter?.loaded
              ? <strong>{result.safe_message_filter.keywords} kw / {result.safe_message_filter.categories} cat</strong>
              : <em>skipped</em>}
          </Text>
        </HStack>
      </Box>

      <Section title="Generic checks"               findings={result.findings?.generic} />
      <Section title="WhatsApp HSM rules"           findings={result.findings?.whatsapp} />
      <Section title="SMS encoding (GSM 03.38)"     findings={result.findings?.sms} />
      <Section title="Safe Message Filter blocklist" findings={result.findings?.safe_message} />
      <Section title="LLM nuance review"            findings={result.findings?.llm_nuance} />

      {allEmpty(result.findings) && (
        <Box border="1px solid" borderColor="green.200" bg="green.50" borderRadius="lg" p={4} textAlign="center">
          <Text color="green.700" fontWeight="500">All checks passed — template is safe to send.</Text>
        </Box>
      )}
    </VStack>
  );
}

function Section({ title, findings }) {
  if (!findings || !findings.length) return null;
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
      <Heading size="xs" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wider">{title} · {findings.length}</Heading>
      <VStack align="stretch" spacing={2}>
        {findings.map((f, i) => (
          <Box key={i} borderLeft="3px solid"
               borderLeftColor={f.severity === "error" ? "red.400" : f.severity === "info" ? "blue.300" : "yellow.400"}
               bg={f.severity === "error" ? "red.50" : f.severity === "info" ? "blue.50" : "yellow.50"}
               p={2} pl={3} borderRadius="sm">
            <Text fontSize="sm" color={f.severity === "error" ? "red.700" : f.severity === "info" ? "blue.700" : "yellow.800"}>
              {f.severity === "error" ? "✗" : f.severity === "info" ? "ℹ" : "⚠"} <Code fontSize="xs">{f.code}</Code> {f.message}
            </Text>
            {f.suggestion && <Text fontSize="xs" color="blue.700" mt={1} fontStyle="italic">→ {f.suggestion}</Text>}
            {f.keyword && <Text fontSize="xs" color="gray.600" mt={1}>blocked keyword: <strong>{f.keyword}</strong>{f.category ? ` (category: ${f.category})` : ""}</Text>}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

function allEmpty(findings) {
  if (!findings) return true;
  return Object.values(findings).every((arr) => !arr || !arr.length);
}
